/**
 * Block Visibility Action
 * Triggered by: arc:action:blockVisibility
 * 
 * This action provides simplified show/hide/toggle functionality specifically
 * for blockBuilder components using the action system.
 */

export const runBlockVisibility = async (data = {}) => {
    try {
        const {
            blockId = '',
            sectionId = '',
            selector = '',
            action = 'toggle', // 'show', 'hide', 'toggle'
            method = 'display', // 'display', 'visibility', 'opacity'
            transition = '0.3s ease',
            fadeAnimation = true
        } = data;

        // Validate action parameter
        const validActions = ['show', 'hide', 'toggle'];
        if (!validActions.includes(action)) {
            throw new Error(`Invalid action. Must be one of: ${validActions.join(', ')}`);
        }

        let targetElements = [];

        // Find target elements by various methods
        if (selector) {
            targetElements = Array.from(document.querySelectorAll(selector));
        } else if (blockId) {
            targetElements = Array.from(document.querySelectorAll(`[data-block-id="${blockId}"]`));
        } else if (sectionId) {
            targetElements = Array.from(document.querySelectorAll(`.section-${sectionId}`));
        }

        if (targetElements.length === 0) {
            throw new Error('No target elements found for visibility action');
        }

        const results = [];

        for (const element of targetElements) {
            try {
                // Get Alpine.js component instance if available
                const alpineComponent = element._x_dataStack?.[0];
                
                if (alpineComponent && typeof alpineComponent.applyInteractionBehavior === 'function') {
                    // Use Alpine component method for consistent behavior
                    const behaviorMap = {
                        'show': 'show-only',
                        'hide': 'hide-only',
                        'toggle': 'toggle'
                    };
                    
                    alpineComponent.applyInteractionBehavior(behaviorMap[action]);
                    
                    results.push({
                        element: element,
                        action: action,
                        method: 'alpine-component',
                        success: true
                    });
                } else {
                    // Fallback to direct DOM manipulation
                    const isCurrentlyVisible = this.isElementVisible(element, method);
                    let shouldShow;

                    switch (action) {
                        case 'show':
                            shouldShow = true;
                            break;
                        case 'hide':
                            shouldShow = false;
                            break;
                        case 'toggle':
                            shouldShow = !isCurrentlyVisible;
                            break;
                    }

                    await this.setElementVisibility(element, shouldShow, method, transition, fadeAnimation);
                    
                    results.push({
                        element: element,
                        action: action,
                        method: method,
                        previousState: isCurrentlyVisible,
                        newState: shouldShow,
                        success: true
                    });
                }
            } catch (elementError) {
                console.error('Error processing element:', element, elementError);
                results.push({
                    element: element,
                    action: action,
                    success: false,
                    error: elementError.message
                });
            }
        }

        return {
            success: true,
            message: `Successfully processed ${results.filter(r => r.success).length} of ${results.length} elements`,
            results: results,
            action: action,
            elementsProcessed: targetElements.length
        };

    } catch (error) {
        console.error('Block visibility action error:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// Helper method to check if element is visible
runBlockVisibility.isElementVisible = function(element, method) {
    const computedStyle = window.getComputedStyle(element);
    
    switch (method) {
        case 'display':
            return computedStyle.display !== 'none';
        case 'visibility':
            return computedStyle.visibility !== 'hidden';
        case 'opacity':
            return parseFloat(computedStyle.opacity) > 0;
        default:
            return computedStyle.display !== 'none';
    }
};

// Helper method to set element visibility
runBlockVisibility.setElementVisibility = async function(element, shouldShow, method, transition, fadeAnimation) {
    // Add transition if not already present and fadeAnimation is enabled
    if (fadeAnimation && !element.style.transition) {
        element.style.transition = transition;
    }

    switch (method) {
        case 'display':
            element.style.display = shouldShow ? (element.dataset.originalDisplay || 'block') : 'none';
            break;
        case 'visibility':
            element.style.visibility = shouldShow ? 'visible' : 'hidden';
            break;
        case 'opacity':
            element.style.opacity = shouldShow ? '1' : '0';
            break;
    }

    // If using fade animation, add a small delay to allow transition to complete
    if (fadeAnimation && method === 'opacity') {
        const transitionDuration = parseFloat(transition) * 1000 || 300;
        await new Promise(resolve => setTimeout(resolve, transitionDuration));
    }
};

export default runBlockVisibility;
